import React from 'react';
import { Badge, BadgeSize, BadgeVariant } from '@usepepper/ui/atoms/badge';
import { BlurView } from 'expo-blur';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import { CardItem, CardItemProps } from '@usepepper/ui/molecules/card-item';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { EqualHeightTouchableOpacity } from './equal-height-touchable-opacity';
import { ListDisplaySize } from '@/contexts/order-guide-settings-context';
import { MAX_VISIBLE_BADGES_CARD } from '@/components/orders/badge-label-list';
import { StyleProp, View, ViewStyle } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useBadgeOverflow } from '@/components/orders/use-badge-overflow';
import { VariantPackInfoContext_GroupBadgeFieldsFragment } from '@/types';

interface PromoBadgeProps {
  label: string;
  size?: `${BadgeSize}`;
  style?: StyleProp<ViewStyle>;
  variant?: `${BadgeVariant}`;
}

export interface OrderableItemCardV2Props {
  badges?: VariantPackInfoContext_GroupBadgeFieldsFragment[];
  bottomContent?: CardItemProps['bottomContent'];
  bottomLeftElement?: CardItemProps['bottomLeftElement'];
  bottomRightElement?: CardItemProps['bottomRightElement'];
  cardStyle?: CardItemProps['style'];
  containerStyle?: StyleProp<ViewStyle>;
  disabled?: boolean;
  focusable?: boolean;
  imageContainerStyle?: StyleProp<ViewStyle>;
  imageProps: CardItemProps['imageProps'];
  listDisplaySize?: ListDisplaySize;
  onPress?: () => void;
  promoBadgeProps?: PromoBadgeProps | null;
  style?: StyleProp<ViewStyle>;
  subheader?: CardItemProps['subheader'];
  title: CardItemProps['title'];
  topLeftElement?: CardItemProps['topLeftElement'];
  topRightElement?: CardItemProps['topRightElement'];
  width?: number;
}

const LIST_DISPLAY_SIZE_TO_BADGE_SIZE: Record<ListDisplaySize, number> = {
  [ListDisplaySize.Small]: 16,
  [ListDisplaySize.Condensed]: 24,
  [ListDisplaySize.Large]: 32,
  [ListDisplaySize.Medium]: 20,
  [ListDisplaySize.Huge]: 32,
  [ListDisplaySize.Standard]: 24,
};

const DEFAULT_PROMO_BADGE_PROPS: Required<
  Pick<PromoBadgeProps, 'size' | 'variant'>
> = {
  size: BadgeSize.MEDIUM,
  variant: BadgeVariant.BLURRED,
};

export const OrderableItemCardV2: React.FC<OrderableItemCardV2Props> = ({
  badges,
  bottomContent,
  bottomLeftElement,
  bottomRightElement,
  cardStyle,
  containerStyle,
  disabled,
  focusable = false,
  imageContainerStyle,
  imageProps,
  listDisplaySize = ListDisplaySize.Standard,
  onPress,
  promoBadgeProps,
  style,
  subheader,
  title,
  topLeftElement,
  topRightElement,
  width,
}) => {
  const [styles] = useStyles();
  const theme = useTheme();

  const resolvedBadgeSize = LIST_DISPLAY_SIZE_TO_BADGE_SIZE[listDisplaySize];

  const resolvedPromoBadge =
    topLeftElement ??
    (promoBadgeProps?.label ? (
      <Badge
        label={promoBadgeProps.label}
        size={promoBadgeProps.size ?? DEFAULT_PROMO_BADGE_PROPS.size}
        style={[styles.promoBadge, promoBadgeProps.style]}
        textStyle={styles.promoText}
        variant={promoBadgeProps.variant ?? DEFAULT_PROMO_BADGE_PROPS.variant}
      />
    ) : null);

  const { overflowCount, visibleBadges } = useBadgeOverflow(
    badges,
    MAX_VISIBLE_BADGES_CARD,
  );

  const resolvedBadges =
    topRightElement ??
    (visibleBadges.length > 0 ? (
      <View style={styles.badgeRow}>
        {visibleBadges.map((badge) => (
          <View key={badge.uuid} style={styles.badgeItem}>
            <BlurView
              experimentalBlurMethod="dimezisBlurView"
              intensity={75}
              style={[
                styles.badgeIconContainer,
                {
                  height: resolvedBadgeSize,
                  width: resolvedBadgeSize,
                },
                badge.background_color
                  ? { backgroundColor: badge.background_color }
                  : null,
              ]}
              tint="light"
            >
              <CachedImage
                alt={`${badge.name} badge`}
                contentFit="contain"
                source={{ uri: badge.image_url }}
                style={styles.badgeIcon}
              />
              {badge.short_name ? (
                <Typography
                  color={theme.sk.color.text.primary}
                  variant="paragraph-sm"
                >
                  {badge.short_name}
                </Typography>
              ) : null}
            </BlurView>
          </View>
        ))}
        {overflowCount > 0 ? (
          <View
            style={[
              styles.overflowContainer,
              {
                height: resolvedBadgeSize,
                width: resolvedBadgeSize,
              },
            ]}
          >
            <Typography variant="paragraph-sm">+{overflowCount}</Typography>
          </View>
        ) : null}
      </View>
    ) : null);

  return (
    <EqualHeightTouchableOpacity
      disabled={disabled || !onPress}
      focusable={focusable}
      onPress={onPress}
      style={[
        containerStyle,
        style,
        typeof width === 'number' ? { width } : null, // TODO: Verify if this is needed after added everywere
      ]}
    >
      <CardItem
        bottomContent={bottomContent}
        bottomLeftElement={bottomLeftElement}
        bottomRightElement={bottomRightElement}
        imageContainerStyle={[styles.imageContainer, imageContainerStyle]}
        imageProps={imageProps}
        style={[styles.card, cardStyle]}
        subheader={subheader}
        title={title}
        topLeftElement={resolvedPromoBadge}
        topRightElement={resolvedBadges}
      />
    </EqualHeightTouchableOpacity>
  );
};

const useStyles = createStyles((theme) => ({
  badgeIcon: {
    borderRadius: theme.sk.radii.radiusSmall,
    height: '100%',
    width: '100%',
  },
  badgeIconContainer: {
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.50)',
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusSmall,
    borderWidth: 0.5,
    justifyContent: 'center',
    overflow: 'hidden',
  },
  badgeItem: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
  },
  badgeRow: {
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
  },
  card: {
    flex: 1,
    justifyContent: 'space-between',
  },
  imageContainer: {
    backgroundColor: theme.sk.color.bg.primary.default,
  },
  overflowContainer: {
    alignItems: 'center',
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusSmall,
    borderWidth: 0.5,
    justifyContent: 'center',
  },
  promoBadge: {
    alignSelf: 'stretch',
    backgroundColor: 'rgba(247, 250, 249, 0.75)',
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusSmall,
    borderWidth: 1,
    height: theme.sk.spacing.spaceLarge,
    maxWidth: '100%',
    paddingHorizontal: theme.sk.spacing.spaceSmall,
  },
  promoText: {
    color: theme.sk.color.text.primary,
    fontSize: 13,
    letterSpacing: 0.26,
    lineHeight: 15.68,
  },
}));
